import { detect } from "detect-browser"
import { UAParser } from "ua-parser-js"
import { flow } from "mobx"
import { printf } from "fast-printf"
import { moment } from "store/util/moment"
import { formatDate} from "utils/IntlManager"
import { localStorage } from "store/util/LocalStorage"
import { appFeatures } from "AppFeatures"
import { l10n } from "store/lang/L10n"

/* previous criteria
	const touchBackend = (browser && ((browser.os === "iOS") || (browser.os === "iPadOS") || isPadOS() || (browser.os === "Android OS"))) ? true : false
*/

const browser = detect()
export const MOBILE_SIZE = 540

export const parsedUa = (): object => {
	const parser = new UAParser(navigator.userAgent)
	return parser.getResult()
}

export const isTouchAvailable = (): boolean => {
	//console.debug("isTouchAvailable ontouchstart %s, maxTouchPoints %s", 'ontouchstart' in window, navigator.maxTouchPoints)
	return ('ontouchstart' in window) ||
		(navigator.maxTouchPoints > 0)
		// only IE10 
		//|| (navigator.msMaxTouchPoints > 0)
}

export const isIpad = ():boolean => {
	// MTV-2818: Since Apple claimed Desktop-Class Browsing with Safari on iPadOS, mobile Safari seems to mimic macOS behavior and user agent.
	// The actual value of maxTouchPoints on the iPad is 5, thus it should be pretty safe.
	let iPad = false
	if (navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2 || navigator.userAgent.match(/iPad/)) {
		iPad = true
	}
	return iPad
}

export const isIphone = (): boolean => {
	return browserName().toLowerCase().indexOf("ios") >= 0
}

export const isSafari = ():boolean => {
	return ((browserName() === "safari") || (browserName() === "ios") || isIpad())
}

export const safariMainVersion = ():number => {
	if (isSafari()) {
		const version = browserVersion().split(".")
		//console.debug("SAFARI VERSION %o", version)
		if (version?.length > 0) {
			return parseInt(version[0])
		}
	}
	return -1
}

export const isMobileOrTablet = ():boolean => {
	// Windows might have touch support devices, Windows Mobile is dead
	console.debug("browserOs %s isTouch %s", browserOs(), isTouchAvailable())
	return isTouchAvailable() && browserOs().indexOf("Windows") < 0
}

export const browserName = ():string => {
	return browser?.name ? browser?.name?.toString() : "n/a"
}

export const browserVersion = ():string => {
	return browser?.version ? browser?.version?.toString() : "n/a"
}

// TODO: add separate methods for OSs to avoid hardcoded strings everywhere, e.g. isAndroid, etc.
export const browserOs = ():string => {
	// fix for Chrome on Android, Chrome reports "Linux" since v???
	if (browser?.os) {
		let os = browser.os.toString()
		if (os?.indexOf("Linux") >= 0) os = "Android or Linux"
		return os
	}
	else return "n/a"
}

export const isAndroid = (): boolean => {
	return browserOs().indexOf("Android") >= 0 || navigator.userAgent.indexOf("Android") >= 0
}

export const isMobileScreenSize = ():boolean => {
	/*
	// eslint-disable-next-line no-alert
	alert("width: " + window.innerWidth + " height: " + window.innerHeight)
	*/

	// On Galaxy S6 Tab Lite, innerHeight in portrait mode is 632 pixels and it shows 1,5 grid rows
	// 540 should be a safe value for the channel list editor in portrait mode
	// (channel list left, 1 row of the grid at the right side)
	//console.debug("isMobileScreenSize", window.innerWidth, window.innerHeight, MOBILE_SIZE, screen.width, screen.height, screenInfo())
	//return Math.min(window.innerWidth, window.innerHeight) < MOBILE_SIZE
	return Math.min(screen.width, screen.height) < MOBILE_SIZE
}


export const isMobileScreenSizeWidth = ():boolean => {
	//return window.innerWidth < MOBILE_SIZE
	return screen.width < MOBILE_SIZE
}

export const isPhoneSize = ():boolean => {
	// css mobileFirst the condition identify not a mobile so need to invert the result of matchMedia(query)
	return !window.matchMedia("(min-width:540px) and (min-height:415px) , not screen and (max-width:823px)").matches
}

export const isDesktop = (): boolean => {
	return (browser?.os?.indexOf("Windows") >= 0 || browser?.os?.indexOf("Mac OS") >= 0) && !isIpad()
}

export const screenInfo = () => {
	let orient = "landscape"
	// https://dev.to/capscode/how-to-detect-screen-orientation-using-javascript-b0m
	// screen.orientation.type not available on Edge/iOS and iPadOS
	//if (screen.orientation.type === "landscape-primary" || screen.orientation.type === "landscape-secondary") {
	// https://stackoverflow.com/questions/5456582/screen-width-and-screen-availwidth-difference-in-javascript#:~:text=width%20%2D%3E%20Returns%20the%20width%20of,pixels%20available%20to%20the%20window.
	if (screen.availWidth < screen.availHeight) orient = "portrait"
	return {
		orientation: orient,
		sWidth: screen.width,
		aWidth: screen.availWidth,
		oWidth: window.outerWidth,
		iWidth: window.innerWidth,
		cWidth: document.body.clientWidth,
		aHeight: screen.availHeight,
		sHeight: screen.height,
		oHeight: window.outerHeight,
		iHeight: window.innerHeight,
		cHeight: document.body.clientHeight
	}
}

export const checkMSE = () => {
	const mse = [{
		id: 'mp4avc',
		codec: 'video/mp4; codecs="avc1.4D4028"',
		supported: false
	}, {
		id: 'mp4ec3',
		codec: 'video/mp4; codecs="ec-3"',
		supported: false
	}, {
		id: 'webm',
		codec: 'video/webm; codecs="vorbis,vp8"',
		supported: false
	}, {
		id: 'mp2t',
		codec: 'video/mp2t; codecs="avc1.42E01E,mp4a.40.2"',
		supported: false
	}]

	let formatted = ''
	for (var i = 0; i < mse?.length; i++) {
		mse[i].supported = false
		if ('MediaSource' in window && MediaSource.isTypeSupported(mse[i].codec))
			mse[i].supported = true
		formatted += printf('id %s, codec %s, supported %s\n', mse[i].id, mse[i].codec, mse[i].supported)
	}

	//console.debug("formatted", formatted)
	return [mse, formatted]
}

// from https://github.com/videojs/videojs-contrib-eme/tree/main#detectsupportedcdms
// calling navigator.requestMediaKeySystemAccess()) can have user-visible effects,
// such as prompting for system resource permissions, which could be disruptive if invoked at inappropriate times.
// use this function sparingly
// See also https://www.radiantmediaplayer.com/blog/detecting-eme-cdm-browser.html and
// https://stackoverflow.com/questions/35086625/determine-drm-system-supported-by-browser
// It might fail if the user is not using https
let _fairplayPromise = new Promise(resolve => {
	const config = {
		/* MTVW-927: use same config as videojs-contrib-eme
		initDataTypes: ['cenc', 'sinf', 'skd'],
		videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }],
		*/
		initDataTypes: ['sinf'],
      videoCapabilities: [{ contentType: 'video/mp4' }]
	}
	// MTVW-927: try / catch block
	try {
		navigator.requestMediaKeySystemAccess('com.apple.fps', [config]).then(response => {
			console.warn("fairplay supported, keySystem %s", response.keySystem)
			resolve(true)
		})
			.catch((e) => {
				console.warn("No fairplay support", e)
				resolve(false)
			})
	}
	catch (e) {
		console.warn("No fairplay support", e)
		resolve(false)
	}
})

export const isFairPlaySupported = flow(function* () {
	// MTVW-927: additional check for eme
	const eme = "MediaKeys" in window || "WebKitMediaKeys" in window || "MSMediaKeys" in window
	if (eme) return yield _fairplayPromise
	else return false
})

let _widevinePromise = new Promise(resolve => {
	const start = performance.now()
	const config = {
		// MTVW-927: use same config as videojs-contrib-eme 
		//initDataTypes: ['cenc'/*, 'sinf', 'skd'*/],
		initDataTypes: ['cenc'],
		audioCapabilities: [{ contentType: 'audio/mp4; codecs="mp4a.40.2"', robustness: 'SW_SECURE_CRYPTO' }],
		// MTVW-927: use same config as videojs-contrib-eme
		//videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.4D401E, avc1.4D4020, avc1.640020, avc1.64002A"', robustness: 'SW_SECURE_CRYPTO' }],
  		videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"', robustness: 'SW_SECURE_CRYPTO' }]
	}
	// MTVW-927: try / catch block
	try {
		navigator.requestMediaKeySystemAccess('com.widevine.alpha', [config]).then(response => {
			const end = performance.now()
			console.warn("widevine supported, keySystem %s, duration:", response.keySystem, end - start, start, end)
			resolve(true)
		})
			.catch((e) => {
				console.warn("No widevine support", e)
				resolve(false)
			})
	}
	catch (e) {
		console.warn("No widevine support", e)
		resolve(false)
	}
})

export const isWidevineSupported = flow(function* () {
	// MTVW-927: additional check for eme
	const eme = "MediaKeys" in window || "WebKitMediaKeys" in window || "MSMediaKeys" in window
	if (eme) return yield _widevinePromise
	else return false
})

export const timeNow = (): string => {
	return moment().local().format("HH:mm:ss.SSS")//new Date().toLocaleTimeString()
}

// to be used with await
export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// MTVW-585: backoff
export const backoff = (retry: number): number => {
	// see https://cloud.google.com/iot/docs/how-tos/exponential-backoff
	// MTVW-963: 32000 -> 5000
	const maxBackoff = 5000
	if (retry < 1) retry = 1
	// MTW-963: 12000 -> 1000 for Math.random() * 1000
	return Math.trunc(Math.min(Math.pow(2, 2 + retry - 1) * 1000 + Math.random() * 1000, maxBackoff))
}

// MTVW-585: shouldRetry
export const shouldRetry = (status: number, retryCount: number): boolean => {
	// https://denalibalser.medium.com/best-practices-for-retry-685bf58de797
	switch (status) {
		case 400: // Bad Request
		case 401: // Unauthorized
		case 403: // Forbidden -> generates mediaError403
		case 404: // Not Found
		case 410: // GONE
		case 500: // Internal Server Error
		case 503: // Service Unavailable
			return false
		default:
			return retryCount !== 0
	}
}

export const getUrlQueryParam = (param: string): any => {
	const searchParams = new URLSearchParams(window.location.href)
	for (const [key, value] of searchParams.entries()) {
		// No URL parsing, see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
		const cKey = key.substring(key.indexOf("?") + 1)
		if (cKey === param) {
			//console.debug("found", cKey, value)
			return value
		}
	}
	return null
}

export const getUpcomingLabelName = (timeStamp: number): string | null => {
	type l10nObject = {
 		[key: string]: string
	}

	const timeCurr = moment().valueOf()
	if (timeStamp && moment(timeStamp).valueOf() > timeCurr) {
		if (moment(timeStamp).startOf("day").valueOf() >= moment().startOf("day").add(2, 'days').valueOf() &&
			moment(timeStamp).startOf("day").valueOf() <= moment().startOf("day").add(7, 'days').valueOf()) {
			// capitalize first letter
			let [first, ...rest] = formatDate(moment(timeStamp), 'dddd')
			return first.toUpperCase() + rest.join('')
		}
		else if (moment(timeStamp).startOf("day").valueOf() >= moment().startOf("day").add(2, 'days').valueOf()) {
			//console.debug("event start date", moment().format("DD.MM"))
			return moment(timeStamp).format("DD.MM.")
		}
		else if (moment(timeStamp).startOf("day").valueOf() >= moment().startOf("day").add(1, 'days').valueOf()) {
			//console.debug("event start morgen")
			return (l10n as l10nObject)?.commonLabelTomorrow
		}
		else if (moment(timeStamp).startOf("day").valueOf() < moment().startOf("day").add(1, 'days').valueOf()) {
			//console.debug("event start heute")
			return (l10n as l10nObject)?.commonLabelToday
		}
	}
	return null
}

export const beep = (): void => {
    const snd = new Audio("data:audio/wav;base64,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")
    snd.play()
}

// MTVW-1141
export const getBrowserLanguage = (): string => {
	// overrideLanguage is used for BDD tests to force a specific language ("de") for the login screen
	const overrideLanguage = localStorage.get("overrideLanguage")
	const supportedLanguages = ["de", "fr", "it", "en", /*"gsw"*/]
	const browserLanguage = overrideLanguage ? overrideLanguage : navigator.language.toLowerCase().substring(0, 2)
	return appFeatures.languageSelectionEnabled && supportedLanguages.includes(browserLanguage) ? browserLanguage : "de"
}

export const safeStringify = (obj: any): string => {
	try {
		return JSON.stringify(obj).replaceAll("\"", "") // re-check replaceAll
	} catch (error) {
		console.error("Failed to stringify object:", error.message)
		return "-"
	}
}
